import { Button, Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskTemplateCardType } from '@econtractor/types';
import { FilesCloudUpload, FilesContainer } from 'modules/files';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import SpinnerBlock from 'components/others/SpinnerBlock';
import RecurrentForm from '../../shared/recurrent/RecurrentForm';
import TaskAnswersEditor from '../../shared/answers/TaskAnswersEditor';
import { useOffTaskDir } from '../model/useOffTaskDir';
import OffTaskDirGeneralTab from './tabs/OffTaskDirGeneralTab';
import OffTaskDirAdditionalTab from './tabs/OffTaskDirAdditionalTab';

interface OffTaskDirProps {
    show: boolean;
    templateId: number | null;
    onHide: () => void;
    onSaved: (template?: TaskTemplateCardType) => void;
    disableRecurrence?: boolean;
    forkFromTemplateId?: number | null;
    /** Другий шар поверх офканваса процесу. */
    stacked?: boolean;
    /** Рядок пишеться в довідник задач, але не з’являється в його списку. */
    hideFromDirectory?: boolean;
}

const OffTaskDir = ({
    show,
    templateId,
    onHide,
    onSaved,
    disableRecurrence = false, 
    forkFromTemplateId = null,
    stacked = false,
    hideFromDirectory = false,
}: OffTaskDirProps) => {
    const { t } = useTranslation(['Tasks', 'common', 'FileNavigator']);
    const {
        form,
        setForm,
        files,
        setFiles,
        pendingFiles,
        setPendingFiles,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        loading,
        saving,
        cloudProgress,
        handleSave,
    } = useOffTaskDir({
        show,
        templateId,
        onHide,
        onSaved,
        disableRecurrence,
        forkFromTemplateId,
        hideFromDirectory,
    });

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            backdrop
            backdropClassName={stacked ? 'process-step-offcanvas-backdrop' : 'task-dir-offcanvas-backdrop'}
            className={stacked ? 'process-step-offcanvas' : 'task-dir-offcanvas'}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    {templateId ? t('directory.titleEdit') : t('directory.titleNew')}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-task-dir-form d-flex flex-column h-100">
                <div className="off-task-dir-content px-3 position-relative">
                <SpinnerBlock show={loading || saving} onlyBlur={saving} />
                <Tabs
                    variant="underline"
                    activeKey={activeTab}
                    onSelect={(tab) => tab && setActiveTab(tab)}
                    className="mb-3 invoice-tab border-bottom"
                >
                    <Tab
                        eventKey="general"
                        title={
                            <>
                                <SolarIcon name="gear" className="me-2 fs-14" />
                                {t('tabGeneral')}
                            </>
                        }
                    >
                        <OffTaskDirGeneralTab
                            form={form}
                            setForm={setForm}
                            saving={saving}
                            showDescription={showDescription}
                            setShowDescription={setShowDescription}
                        />
                    </Tab>
                    <Tab
                        eventKey="additional"
                        title={
                            <>
                                <SolarIcon name="card-list" className="me-2 fs-14" />
                                {t('tabAdditional')}
                            </>
                        }
                    >
                        <OffTaskDirAdditionalTab form={form} setForm={setForm} saving={saving} />
                    </Tab>
                    <Tab
                        eventKey="answers"
                        title={
                            <>
                                <SolarIcon name="list-ul" className="me-2 fs-14" />
                                {t('tabAnswers')}
                            </>
                        }
                    >
                        <TaskAnswersEditor
                            value={form.answers}
                            disabled={saving}
                            onChange={(answers) => setForm((prev) => ({ ...prev, answers }))}
                        />
                    </Tab>
                    {disableRecurrence ? null : (
                    <Tab
                        eventKey="recurrent"
                        title={
                            <>
                                <SolarIcon name="arrow-repeat" className="me-2 fs-14" />
                                {t('tabRecurrent')}
                            </>
                        }
                    >
                        <RecurrentForm
                            idPrefix="dir-task" 
                            disabled={saving}
                            showAllowOverlap={form.reserveExecutorTime}
                            monthDayHint={1}
                            value={{
                                enabled: form.recurrentEnabled,
                                data: form.recurrentData,
                                allowOverlap: form.recurrentAllowOverlap,
                            }}
                            onChange={(next) =>
                                setForm((prev) => ({
                                    ...prev,
                                    recurrentEnabled: next.enabled,
                                    recurrentData: next.data,
                                    recurrentAllowOverlap: next.allowOverlap,
                                }))
                            }
                        />
                    </Tab>
                    )}
                    <Tab
                        eventKey="files"
                        title={
                            <>
                                <SolarIcon name="paperclip" className="me-2 fs-14" />
                                {t('tabFiles')}
                            </>
                        }
                    >
                        {templateId ? (
                            <FilesContainer
                                files={files}
                                scope={{ taskTemplateId: templateId }}
                                imageColProps={{ xs: 6, md: 4 }}
                                canEdit={!forkFromTemplateId}
                                addButtonLabel={t('btnAttach')}
                                uploadingLabel={t('common:buttons.uploading')}
                                saveFirstMessage=""
                                errorMessage={t('common:messError')}
                                uploadSuccessMessage={t('FileNavigator:messDownloadSuccess')}
                                onFilesChange={async (next) => {
                                    setFiles(next);
                                    return true;
                                }}
                            />
                        ) : (
                            <FilesCloudUpload
                                disabled={saving}
                                progress={cloudProgress}
                                files={pendingFiles}
                                setFiles={setPendingFiles}
                                label={t('labelFiles')}
                                buttonLabel={t('btnAttach')}
                                uploadingLabel={t('common:buttons.uploading')}
                            />
                        )}
                    </Tab>
                </Tabs>
                </div>
                <div className="p-3 offcanvas-footer off-task-dir-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="info"
                            className="min100px"
                            disabled={saving || loading}
                            onClick={() => void handleSave()}
                        >
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="check2-all" className="fs-14 me-2" />
                                    {t('common:buttons.save')}
                                </>,
                                saving,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffTaskDir;
